import React, { useState, useEffect } from "react";
import {
  ClockIcon,
  BoltIcon,
  MoonIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import WorkSummary from "pages/employee/WorkSummary";
import {
  fetchSummaryDetails,
  formatDuration,
  SummaryDataset,
} from "pages/employee/WorkDetails";
import apiService from "services/apiService";

// ============================================
// INTERFACES & TYPES
// ============================================

interface StatCard {
  id: string;
  title: string;
  value: string;
  subtitle: string;
  icon: JSX.Element;
  bgColor: string;
  iconBgColor: string;
  textColor: string;
}

interface Application {
  id: number;
  name: string;
  duration: string;
  percentage: number;
  color: string;
}

interface User {
  id: string;
  email: string;
  name: string;
}

interface ApiUser {
  email: string;
  displayName: string;
}

interface ApiTopApp {
  applicationName: string;
  totalDuration: number;
  usagePercentage: number;
}

// ============================================
// STATIC DATA (Easily replaceable with API)
// ============================================

const STATIC_TRACKING_DATA = {
  totalTime: "3h 33m",
  totalSessions: 543,
  activeTime: "3h 18m",
  activePercentage: 93.1,
  idleTime: "14m",
  idlePercentage: 6.9,
  appsUsed: 8,
  date: "12-12-2025",
};

const STATIC_APPLICATIONS: Application[] = [
  {
    id: 1,
    name: "ShellHost",
    duration: "1h 18m",
    percentage: 41,
    color: "bg-indigo-500",
  },
  {
    id: 2,
    name: "chrome",
    duration: "1h 13m",
    percentage: 37,
    color: "bg-indigo-500",
  },
  {
    id: 3,
    name: "Code",
    duration: "32m",
    percentage: 17,
    color: "bg-indigo-500",
  },
  {
    id: 4,
    name: "ms-teams",
    duration: "8m",
    percentage: 4,
    color: "bg-indigo-500",
  },
  {
    id: 5,
    name: "msedge",
    duration: "3m",
    percentage: 2,
    color: "bg-indigo-500",
  },
];

// ============================================
// THEME CONFIGURATION
// ============================================

const THEME = {
  stats: {
    totalTime: {
      bgColor: "bg-blue-50",
      iconBgColor: "bg-blue-100",
      textColor: "text-blue-600",
      borderColor: "border-blue-200",
    },
    activeTime: {
      bgColor: "bg-green-50",
      iconBgColor: "bg-green-100",
      textColor: "text-green-600",
      borderColor: "border-green-200",
    },
    idleTime: {
      bgColor: "bg-yellow-50",
      iconBgColor: "bg-yellow-100",
      textColor: "text-yellow-600",
      borderColor: "border-yellow-200",
    },
    appsUsed: {
      bgColor: "bg-purple-50",
      iconBgColor: "bg-purple-100",
      textColor: "text-purple-600",
      borderColor: "border-purple-200",
    },
  },
  progressBar: {
    active: "bg-green-500",
    idle: "bg-yellow-500",
    background: "bg-gray-200",
  },
  application: {
    bar: "bg-indigo-500",
    background: "bg-gray-200",
  },
};

// ============================================
// MAIN COMPONENT
// ============================================

const TrackingDashboard = () => {
  const [selectedDate] = useState(STATIC_TRACKING_DATA.date);
  const [users, setUsers] = useState<User[]>([
    { id: "all", email: "all@users.com", name: "All Users" },
  ]);
  const [selectedUser, setSelectedUser] = useState<User>({
    id: "all",
    email: "all@users.com",
    name: "All Users",
  });
  const [activeTab, setActiveTab] = useState("Summary");
  const [summaryData, setSummaryData] = useState<SummaryDataset | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [applications, setApplications] =
    useState<Application[]>(STATIC_APPLICATIONS);

  // Retrieve the manager's JWT token (Adjust this to match how you store your token, e.g., Redux, Context, etc.)
  const managerJwt = typeof window !== 'undefined' ? localStorage.getItem("accessToken") || "" : "";

  // Tabs configuration (Added Live Stream)
  const tabs = [
    "Summary",
    "Sessions",
    "URL Sessions",
    "Device Status",
    "Screenshots",
    "Live Stream"
  ];

  // Fetch users from API
  useEffect(() => {
    setLoadingUsers(true);
    apiService
      .get("api/users")
      .then((res) => {
        console.log("usersss", res.data);

        // Transform API response to User format
        const transformedUsers: User[] = res.data.map((user: ApiUser) => ({
          id: user.email, // Using email as ID
          email: user.email,
          name: user.displayName,
        }));

        // Add "All Users" option at the beginning
        const allUsers: User[] = [
          { id: "all", email: "all@users.com", name: "All Users" },
          ...transformedUsers,
        ];

        setUsers(allUsers);
        console.log("Loaded users:", allUsers);
      })
      .catch((err) => {
        console.log("Error fetching users:", err);
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }, []);

  // Fetch summary data when user changes or date changes
  useEffect(() => {
    const fetchData = async () => {
      // If "All Users" is selected, show static data or aggregate data
      if (selectedUser.id === "all") {
        setSummaryData(null); // Reset to show static data
        setApplications(STATIC_APPLICATIONS);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchSummaryDetails(selectedUser.email);
        if (data) {
          setSummaryData(data);

          // Transform top5Apps to Application format
          if (data.top5AppsDisplay && data.top5AppsDisplay.length > 0) {
            const transformedApps: Application[] = data.top5AppsDisplay.map(
              (app: ApiTopApp, index: number) => ({
                id: index + 1,
                name: app.applicationName,
                duration: formatDuration(app.totalDuration),
                percentage: Math.round(app.usagePercentage),
                color: "bg-indigo-500",
              })
            );
            setApplications(transformedApps);
          }
        }
      } catch (error) {
        console.error("Error fetching summary data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedUser, selectedDate]);

  // Generate stat cards dynamically based on selected user
  const getStatCardsData = () => {
    if (summaryData && selectedUser.id !== "all") {
      return {
        totalTime: formatDuration(summaryData.totalTime),
        totalSessions: summaryData.sessionCount,
        activeTime: formatDuration(summaryData.activeTime),
        activePercentage: summaryData.activePercentage.toFixed(1),
        idleTime: formatDuration(summaryData.idleTime),
        idlePercentage: summaryData.idlePercentage.toFixed(1),
        appsUsed: summaryData.uniqueApplications,
      };
    }
    return STATIC_TRACKING_DATA;
  };

  const statsData = getStatCardsData();

  // Map active tab to WorkSummary view (Updated return type & switch)
  const getWorkSummaryView = ():
    | "summary"
    | "sessions"
    | "urlSessions"
    | "screenshots"
    | "deviceStatus"
    | "faceRecognition"
    | "liveStream" => {
    switch (activeTab) {
      case "Sessions":
        return "sessions";
      case "URL Sessions":
        return "urlSessions";
      case "Screenshots":
        return "screenshots";
      case "Device Status":
        return "deviceStatus";
      case "Live Stream":
        return "liveStream";
      default:
        return "summary";
    }
  };

  const statCards: StatCard[] = [
    {
      id: "total-time",
      title: "Total Time",
      value: statsData.totalTime,
      subtitle: `${statsData.totalSessions} sessions`,
      icon: <ClockIcon className="w-6 h-6" />,
      bgColor: THEME.stats.totalTime.bgColor,
      iconBgColor: THEME.stats.totalTime.iconBgColor,
      textColor: THEME.stats.totalTime.textColor,
    },
    {
      id: "active-time",
      title: "Active Time",
      value: statsData.activeTime,
      subtitle: `${statsData.activePercentage}% of total`,
      icon: <BoltIcon className="w-6 h-6" />,
      bgColor: THEME.stats.activeTime.bgColor,
      iconBgColor: THEME.stats.activeTime.iconBgColor,
      textColor: THEME.stats.activeTime.textColor,
    },
    {
      id: "idle-time",
      title: "Idle Time",
      value: statsData.idleTime,
      subtitle: `${statsData.idlePercentage}% of total`,
      icon: <MoonIcon className="w-6 h-6" />,
      bgColor: THEME.stats.idleTime.bgColor,
      iconBgColor: THEME.stats.idleTime.iconBgColor,
      textColor: THEME.stats.idleTime.textColor,
    },
    {
      id: "apps-used",
      title: "Apps Used",
      value: statsData.appsUsed.toString(),
      subtitle: "Unique applications",
      icon: <Square3Stack3DIcon className="w-6 h-6" />,
      bgColor: THEME.stats.appsUsed.bgColor,
      iconBgColor: THEME.stats.appsUsed.iconBgColor,
      textColor: THEME.stats.appsUsed.textColor,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            {/* Left side: Back button and Title */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Activity Tracking
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedUser.email}
                </p>
              </div>
            </div>

            {/* Right side: Date picker and User selector */}
            <div className="flex items-center gap-4">
              {/* Date Filter */}

              {/* User Selector Dropdown */}
              <div className="flex items-center gap-2">
                <select
                  value={selectedUser.id}
                  onChange={(e) => {
                    const user = users.find(
                      (u: User) => u.id === e.target.value
                    );
                    if (user) setSelectedUser(user);
                  }}
                  className="px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer hover:bg-gray-50 transition-colors min-w-[200px]"
                  disabled={loadingUsers}
                >
                  {loadingUsers ? (
                    <option>Loading users...</option>
                  ) : (
                    users.map((user: User) => (
                      <option key={user.id} value={user.id}>
                        {user.id === "all" ? "All Users" : user.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Sessions Badge */}
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex gap-8">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 px-1 font-medium text-sm transition-colors relative ${activeTab === tab
                      ? "text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12 mb-6">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading data...</p>
            </div>
          </div>
        )}

        {/* Show Summary Tab Content */}
        {!loading && activeTab === "Summary" && (
          <>
            {/* Stats Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {statCards.map((card) => (
                <div
                  key={card.id}
                  className={`${card.bgColor} rounded-xl p-5 border border-opacity-50 transition-all hover:shadow-md`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-1">
                        {card.title}
                      </p>
                      <h3 className="text-3xl font-bold text-gray-900">
                        {card.value}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {card.subtitle}
                      </p>
                    </div>
                    <div className={`${card.iconBgColor} p-3 rounded-lg`}>
                      <div className={card.textColor}>{card.icon}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Active vs Idle Time Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">
                Active vs Idle Time
              </h2>

              {/* Active Time Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">
                    Active: {statsData.activeTime}
                  </span>
                  <span className="text-sm font-bold text-green-600">
                    {statsData.activePercentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className={`${THEME.progressBar.active} h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold transition-all duration-500`}
                    style={{ width: `${statsData.activePercentage}%` }}
                  >
                    {statsData.activePercentage}%
                  </div>
                </div>
              </div>

              {/* Idle Time Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">
                    Idle: {statsData.idleTime}
                  </span>
                  <span className="text-sm font-bold text-yellow-600">
                    {statsData.idlePercentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className={`${THEME.progressBar.idle} h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold transition-all duration-500`}
                    style={{ width: `${statsData.idlePercentage}%` }}
                  >
                    {statsData.idlePercentage}%
                  </div>
                </div>
              </div>
            </div>

            {/* Top 5 Most Used Applications */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-5">
                Top 5 Most Used Applications
              </h2>

              <div className="space-y-4">
                {applications.length > 0 ? (
                  applications.map((app, index) => (
                    <div key={app.id} className="flex items-center gap-4">
                      {/* Rank Number */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-gray-600">
                          {index + 1}
                        </span>
                      </div>

                      {/* App Name and Progress Bar */}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-800">
                            {app.name}
                          </span>
                          <span className="text-sm font-medium text-gray-600">
                            {app.duration}
                          </span>
                        </div>
                        <div className="relative w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                          <div
                            className={`${app.color} h-6 rounded-full transition-all duration-500 flex items-center px-3`}
                            style={{ width: `${app.percentage}%` }}
                          >
                            <span className="text-xs font-bold text-white">
                              {app.percentage}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No application data available
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Show WorkSummary for other tabs when specific user is selected */}
        {!loading && activeTab !== "Summary" && selectedUser.id !== "all" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <WorkSummary
              employeeId={selectedUser.id}
              employeeName={selectedUser.name}
              employeeEmail={selectedUser.email}
              hideNavigation={true}
              initialView={getWorkSummaryView()}
              managerJwt={managerJwt}
            />
          </div>
        )}

        {/* Show message when trying to view other tabs with "All Users" selected */}
        {!loading && activeTab !== "Summary" && selectedUser.id === "all" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="text-center">
              <svg
                className="w-16 h-16 text-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Select a User
              </h3>
              <p className="text-gray-600">
                Please select a specific user to view detailed{" "}
                {activeTab.toLowerCase()} data
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingDashboard;